import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";

// The public page for our text messages (Karl, 2026-11-04): carriers ask
// for a live page that shows how customers opt in, what we send, and how
// to stop. The screenshot is the real sign-in form (engine/GuestSignIn.js);
// keep the consent wording below in step with it.

export const CONSENT_TEXT =
	"I agree to receive text messages from Scerv and the restaurant I'm ordering from: sign-in codes and updates about my order, reservation, waitlist and tab. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help. No marketing texts.";

const Section = styled.section`
	padding: ${({ theme }) => theme.spacing.xl} 0;
	background-color: ${({ theme }) => theme.colors.background};
`;

const Container = styled.div`
	max-width: 760px;
	margin: 0 auto;
	background-color: ${({ theme }) => theme.colors.white};
	border-radius: ${({ theme }) => theme.radius.md};
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	padding: ${({ theme }) => theme.spacing.lg};
`;

const H1 = styled.h1`
	font-size: 2.2rem;
	margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const H2 = styled.h2`
	font-size: 1.4rem;
	margin-top: ${({ theme }) => theme.spacing.lg};
	margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const P = styled.p`
	font-size: 1rem;
	line-height: 1.6;
	margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const List = styled.ul`
	list-style: disc;
	margin-left: ${({ theme }) => theme.spacing.lg};
	margin-bottom: ${({ theme }) => theme.spacing.md};
	line-height: 1.6;
`;

const Quote = styled.blockquote`
	border-left: 4px solid ${({ theme }) => theme.colors.primary || "#0f766e"};
	margin: 0 0 ${({ theme }) => theme.spacing.md};
	padding: 8px 16px;
	background: #f4f7f7;
	line-height: 1.6;
`;

const Shot = styled.img`
	display: block;
	width: 100%;
	max-width: 360px;
	border: 1px solid #d9dee4;
	border-radius: 16px;
	margin: 0 0 ${({ theme }) => theme.spacing.md};
`;

const TextMessaging = () => (
	<Section>
		<Helmet>
			<title>Text messages · Scerv</title>
			<meta name="description" content="How Scerv text messages work: how customers opt in, what we send, and how to stop." />
		</Helmet>
		<Container>
			<H1>Scerv text messages</H1>
			<P>
				Scerv runs online ordering, reservations and waitlists for restaurants.
				We text customers only about something they asked for. We never send
				marketing texts.
			</P>

			<H2>What we send</H2>
			<List>
				<li>A one-time sign-in code when you sign in with your mobile number.</li>
				<li>Updates about your order, such as "your pickup order is ready".</li>
				<li>A link to place a catering order that you asked a restaurant for.</li>
				<li>Updates about your reservation, waitlist spot or bar tab.</li>
			</List>

			<H2>How you opt in</H2>
			<P>
				<strong>Online:</strong> when you order, join a waitlist or open a tab on
				scerv.com, you enter your mobile number and check this consent box. It
				is never checked for you, and you can't continue without checking it.
				The box reads, word for word:
			</P>
			<Quote>{CONSENT_TEXT}</Quote>
			<Shot
				src="/sms-opt-in.png"
				alt="The Scerv sign-in form: first name, mobile number, an unchecked box agreeing to texts, and a Text me a code button that stays off until the box is checked."
			/>
			<P>
				<strong>At the restaurant:</strong> when you call or visit to order
				catering, staff enter your name and mobile number only at your request,
				so the restaurant can text you the link to build and pay for your order.
			</P>

			<H2>Frequency, cost and stopping</H2>
			<List>
				<li>Message frequency varies with your orders and bookings.</li>
				<li>Message and data rates may apply.</li>
				<li>Reply <strong>STOP</strong> to any message to stop all texts. Reply <strong>HELP</strong> for help.</li>
				<li>Carriers are not liable for delayed or undelivered messages.</li>
			</List>

			<H2>Your privacy</H2>
			<P>
				No mobile information will be shared with third parties or affiliates
				for marketing or promotional purposes. Text messaging opt-in data and
				consent will not be shared with any third parties. Read our{" "}
				<a href="/privacy-policy">Privacy Policy</a> and{" "}
				<a href="/terms-of-service">Terms of Service</a>.
			</P>

			<H2>Help</H2>
			<P>Questions about texts: support@scerv.com</P>
		</Container>
	</Section>
);

export default TextMessaging;
